import { useState } from "react";
import { MapPin, Plus, Edit, Trash2, Home, Briefcase, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAddresses,
  useCreateAddressMutation,
  useDeleteAddressMutation,
  useSetDefaultAddressMutation,
  useUpdateAddressMutation,
} from "@/hooks/queries";
import type { AddressResponseDto } from "@/types/address.dto";
import { AddressType } from "@/types/enums";
import { PATNA_COORDS } from "@/contexts/LocationContext";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/services/httpClient";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/shared";

const iconMap: Record<AddressType, typeof Home> = {
  [AddressType.Home]: Home,
  [AddressType.Work]: Briefcase,
  [AddressType.Other]: Users,
};

const emptyForm = {
  type: String(AddressType.Home),
  name: "",
  phone: "",
  address: "",
  state: "Bihar",
  city: "Patna",
  pincode: "",
  isDefault: false,
};

function mapTypeLabel(type: AddressType): string {
  switch (type) {
    case AddressType.Work:
      return "Office";
    case AddressType.Other:
      return "Other";
    default:
      return "Home";
  }
}

function parseType(value: string): AddressType {
  switch (value) {
    case String(AddressType.Work):
      return AddressType.Work;
    case String(AddressType.Other):
      return AddressType.Other;
    default:
      return AddressType.Home;
  }
}

const AccountAddresses = () => {
  const { data: addresses = [], isLoading } = useAddresses();
  const createAddress = useCreateAddressMutation();
  const updateAddress = useUpdateAddressMutation();
  const deleteAddress = useDeleteAddressMutation();
  const setDefaultAddress = useSetDefaultAddressMutation();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AddressResponseDto | null>(null);
  const [form, setForm] = useState(emptyForm);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (addr: AddressResponseDto) => {
    setEditing(addr);
    setForm({
      type: String(addr.type),
      name: addr.fullName,
      phone: addr.phone,
      address: addr.addressLine1,
      state: addr.state,
      city: addr.city,
      pincode: addr.pincode,
      isDefault: addr.isDefault,
    });
    setDialogOpen(true);
  };

  const buildDto = () => ({
    fullName: form.name.trim(),
    phone: form.phone.trim(),
    addressLine1: form.address.trim(),
    city: form.city.trim(),
    state: form.state.trim(),
    pincode: form.pincode.trim(),
    country: "India",
    latitude: PATNA_COORDS.latitude,
    longitude: PATNA_COORDS.longitude,
    type: parseType(form.type),
    isDefault: form.isDefault,
  });

  const handleSave = async () => {
    if (!form.name.trim() || !form.address.trim() || !form.pincode.trim() || !form.phone.trim()) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }

    try {
      if (editing) {
        await updateAddress.mutateAsync({ id: editing.id, dto: buildDto() });
        toast({ title: "Address updated" });
      } else {
        await createAddress.mutateAsync(buildDto());
        toast({ title: "Address added" });
      }
      setDialogOpen(false);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteAddress.mutateAsync(id);
      toast({ title: "Address removed", variant: "destructive" });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await setDefaultAddress.mutateAsync(id);
      toast({ title: "Default address updated" });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Addresses"
        description="Manage your delivery addresses."
        actions={
          <Button className="gap-2" onClick={openAdd}>
            <Plus className="h-4 w-4" />
            Add Address
          </Button>
        }
      />

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {addresses.map((addr) => {
            const Icon = iconMap[addr.type] || MapPin;
            return (
              <Card key={addr.id} className={addr.isDefault ? "border-primary/30 ring-1 ring-primary/20" : ""}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{mapTypeLabel(addr.type)}</p>
                        {addr.isDefault && <Badge variant="default" className="text-[10px] h-4 px-1.5 mt-0.5">Default</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="font-medium">{addr.fullName}</p>
                    <p className="text-muted-foreground text-xs">{addr.addressLine1}</p>
                    <p className="text-muted-foreground text-xs">{addr.city} - {addr.pincode}</p>
                    <p className="text-muted-foreground text-xs">{addr.phone}</p>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => openEdit(addr)}>
                      <Edit className="h-3 w-3" /> Edit
                    </Button>
                    {!addr.isDefault && (
                      <>
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => handleSetDefault(addr.id)}>
                          Set Default
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleDelete(addr.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Address" : "Add Address"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Label</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(AddressType.Home)}>Home</SelectItem>
                  <SelectItem value={String(AddressType.Work)}>Office</SelectItem>
                  <SelectItem value={String(AddressType.Other)}>Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Phone *</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-2"><Label>Address *</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div className="space-y-2"><Label>Pincode *</Label><Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Set as default</Label>
              <Switch checked={form.isDefault} onCheckedChange={(v) => setForm({ ...form, isDefault: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createAddress.isPending || updateAddress.isPending}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountAddresses;
